import { AppState } from './types';
import React, { useState, useMemo, useEffect } from 'react';
import Papa from 'papaparse';
import { read, utils, writeFile } from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { Upload, Map, Download, Settings, Activity, Cpu, FileText, MapPin, Car, AlertTriangle, FileJson, FileDown, Ban, UserX, CheckCircle2, Sun, Moon, Globe, Layers, ArrowRight, TrendingUp, Zap, LayoutDashboard, Filter, PanelRightClose, PanelRightOpen, History, Loader2, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';

import { processRideData, generateCSVContent, optimizeFleetDistribution, generateJSONContent, generateStatusCSV, buildStatusClusters, generatePickupDropPointCSV, generateRawDataWithHexCSV, generateHexIdsData, VALID_HEX_IDS } from './h3Helper';
import { getPacingStrategy } from './geminiService';
import { Spinner } from './Spinner';
import { MapVisualizer } from './MapVisualizer';
import { DateRangePicker } from './DateRangePicker';
import './hex-hotspots.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HexHotspots = () => {
    const [appState, setAppState] = useState(AppState.UPLOAD);

    // Cluster Data Buckets
    const [baseClustersAll, setBaseClustersAll] = useState([]);
    const [baseClustersMorning, setBaseClustersMorning] = useState([]);
    const [baseClustersEvening, setBaseClustersEvening] = useState([]);

    // UI State
    const [timeView, setTimeView] = useState('ALL');
    const [startHour, setStartHour] = useState('ALL');
    const [endHour, setEndHour] = useState('ALL');
    const [mapMetric, setMapMetric] = useState('DEMAND');
    const [locationType, setLocationType] = useState('PICKUP');
    const [mapStatusFilter, setMapStatusFilter] = useState(['ALL']);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sortField, setSortField] = useState('demandScore');
    const [sortDirection, setSortDirection] = useState('desc');

    // Service Area overlay toggle
    const [showServiceArea, setShowServiceArea] = useState(false);

    // Weird Area overlay toggle
    const [showWeirdArea, setShowWeirdArea] = useState(false);

    // Weird Area coordinates (can be modified by user)
    const [weirdAreaCoords, setWeirdAreaCoords] = useState(null);

    // Hex opacity for transparency control
    const [hexOpacity, setHexOpacity] = useState(0.7);

    // KMZ Overlay state
    const [kmzOverlay, setKmzOverlay] = useState(null);

    // Sidebar state - collapsed by default
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Draft states for sidebar filters (applied only when "Apply" is clicked)
    const [draftTimeView, setDraftTimeView] = useState('ALL');
    const [draftStartHour, setDraftStartHour] = useState('ALL');
    const [draftEndHour, setDraftEndHour] = useState('ALL');
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');

    // Sync draft states when sidebar opens
    useEffect(() => {
        if (isSidebarOpen) {
            setDraftTimeView(timeView);
            setDraftStartHour(startHour);
            setDraftEndHour(endHour);
            setDraftStartDate(startDate);
            setDraftEndDate(endDate);
        }
    }, [isSidebarOpen]);

    // Apply filters function
    const applyFilters = () => {
        setTimeView(draftTimeView);
        setStartHour(draftStartHour);
        setEndHour(draftEndHour);
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
        setIsSidebarOpen(false);
    };

    // Keep raw data for status filtering
    const [rawRideData, setRawRideData] = useState([]);

    // Previous file metadata and loading state
    const [previousFileMetadata, setPreviousFileMetadata] = useState(null);
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
    const [isCheckingPrevious, setIsCheckingPrevious] = useState(true);

    // Check for previous data on mount
    useEffect(() => {
        const checkPreviousData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setIsCheckingPrevious(false);
                return;
            }

            try {
                const response = await axios.get(`${API}/hex-hotspots/check-previous-data`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success && response.data.hasPreviousData) {
                    setPreviousFileMetadata({
                        ...response.data.metadata,
                        isDefault: response.data.isDefault || false
                    });
                }
            } catch (error) {
                console.error('Error checking previous data:', error);
            } finally {
                setIsCheckingPrevious(false);
            }
        };

        checkPreviousData();
    }, []);

    // NOTE: Removed auto-load from localStorage - users should always start at upload screen
    // and explicitly click "Load Previous File" to load data

    // Function to save data to localStorage and backend
    const saveDataToStorage = async (data, filename = 'Unknown') => {
        // NOTE: localStorage save removed as it causes QuotaExceededError and is not read anywhere


        // Save to backend (persistent storage)
        const token = localStorage.getItem('token');
        if (token) {
            try {
                await axios.post(`${API}/hex-hotspots/save-ride-data`, {
                    rideData: data,
                    filename: filename
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Update previous file metadata
                setPreviousFileMetadata({
                    filename: filename,
                    recordCount: data.length,
                    uploadedAt: new Date().toISOString(),
                    isDefault: false
                });
            } catch (error) {
                console.error('Error saving data to backend:', error);
            }
        }
    };

    // Function to load previous file from backend
    const handleLoadPreviousFile = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to load previous data');
            return;
        }

        setIsLoadingPrevious(true);

        try {
            // Use the load-previous-data endpoint which handles both user and default data
            const endpoint = `${API}/hex-hotspots/load-previous-data`;

            const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.ride_data) {
                const rideData = response.data.ride_data;
                const filename = response.data.filename;

                setRawRideData(rideData);

                setRawRideData(rideData);

                // NOTE: localStorage save removed as it is not used and causes potential valid-but-unused writes

                // Process the data
                const processed = processRideData(rideData, config, undefined, locationType, startHour, endHour);
                setBaseClustersAll(processed.allDay);
                setBaseClustersMorning(processed.morning);
                setBaseClustersEvening(processed.evening);
                setDebugStats(processed.debugStats);

                setAppState(AppState.DASHBOARD);
            } else {
                alert(response.data.message || 'No previous data found');
            }
        } catch (error) {
            console.error('Error loading previous data:', error);
            alert('Failed to load previous data');
        } finally {
            setIsLoadingPrevious(false);
        }
    };

    // Function to upload new file (reset to upload state)
    const handleUploadNewFile = () => {
        setAppState(AppState.UPLOAD);
    };

    // Status dropdown open state
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (statusDropdownOpen && !e.target.closest('.status-dropdown-container')) {
                setStatusDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [statusDropdownOpen]);

    const [config, setConfig] = useState({
        h3Resolution: 8,
        targetSlaMinutes: 5,
        avgTripTimeMinutes: 25,
        driverEfficiency: 0.85
    });

    // Fleet Optimization State
    const [isFleetConstrained, setIsFleetConstrained] = useState(false);
    const [fleetSize, setFleetSize] = useState(0);

    // Debug Stats
    const [debugStats, setDebugStats] = useState(null);

    const [aiInsight, setAiInsight] = useState("");
    const [loadingAi, setLoadingAi] = useState(false);

    // Select the correct base clusters based on the tab view
    const currentBaseClusters = useMemo(() => {
        switch (timeView) {
            case 'MORNING': return baseClustersMorning;
            case 'EVENING': return baseClustersEvening;
            default: return baseClustersAll;
        }
    }, [timeView, baseClustersAll, baseClustersMorning, baseClustersEvening]);

    // Derived clusters based on fleet constraints for the CURRENT VIEW
    const displayClusters = useMemo(() => {
        if (currentBaseClusters.length === 0) return [];

        // 1. Filter Logic (Fish Bowl)
        let clusters = [...currentBaseClusters];
        if (mapStatusFilter.includes('FISH_BOWL')) {
            clusters = clusters.filter(c => VALID_HEX_IDS.has(c.hexId));
        }

        // 2. NO Optimization for Main Map (Visualization Only)
        // Just return the clusters as they are (with ideal supply)

        // Apply sorting
        return clusters.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            if (sortField === 'hexId') {
                aVal = aVal?.toString() || '';
                bVal = bVal?.toString() || '';
            }

            if (aVal === bVal) return 0;
            const comparison = aVal > bVal ? 1 : -1;
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [currentBaseClusters, mapStatusFilter, sortField, sortDirection]);

    // Separate Optimization Logic (Strictly Fish Bowl)
    const optimizedClusters = useMemo(() => {
        if (currentBaseClusters.length === 0) return [];
        // optimizeFleetDistribution now internally filters for VALID_HEX_IDS
        return optimizeFleetDistribution(currentBaseClusters, isFleetConstrained ? fleetSize : null);
    }, [currentBaseClusters, isFleetConstrained, fleetSize]);

    // Map clusters can be switched between overall and status-based views
    const mapClusters = useMemo(() => {
        if (mapMetric === 'SLA') {
            return displayClusters;
        }

        // Demand view - filter by status if needed
        // If ALL is selected, OR if FISH_BOWL is the only selection (which implies ALL spatially filtered)
        if (mapStatusFilter.includes('ALL') || (mapStatusFilter.length === 1 && mapStatusFilter.includes('FISH_BOWL'))) {
            return displayClusters;
        } else {
            const dateFilter = startDate || endDate ? {
                startDate: startDate ? new Date(startDate) : null,
                endDate: endDate ? new Date(endDate) : null,
                timeView: timeView,
                selectedDates: []
            } : undefined;

            return buildStatusClusters(
                rawRideData,
                mapStatusFilter,
                config,
                locationType,
                dateFilter,
                startHour,
                endHour
            );
        }
    }, [mapMetric, mapStatusFilter, displayClusters, rawRideData, config, locationType, startDate, endDate, timeView, startHour, endHour]);

    // Calculate totals based on what is actually shown on the map (mapClusters)
    // This ensures the top-line "Total Demand" matches the same filtered slice the user sees.
    const totalDemand = useMemo(() =>
        mapClusters.reduce((sum, c) => sum + (c.demand || 0), 0)
        , [mapClusters]);
    const totalIdealSupply = useMemo(() => mapClusters.reduce((sum, c) => sum + c.requiredSupply, 0), [mapClusters]);

    // Allocated Supply comes from OPTIMIZED clusters
    const totalAllocatedSupply = useMemo(() => optimizedClusters.reduce((sum, c) => sum + c.allocatedSupply, 0), [optimizedClusters]);

    const avgSlaScore = useMemo(() => {
        if (optimizedClusters.length === 0) return 0;
        const sumScore = optimizedClusters.reduce((sum, c) => sum + c.slaComplianceScore, 0);
        return Math.round(sumScore / optimizedClusters.length);
    }, [optimizedClusters]);

    // Status Counts - filtered by selected date range
    const dateFilteredStats = useMemo(() => {
        const counts = { completed: 0, cancelled: 0, driverNotFound: 0 };

        // If no date filter is set, don't show any counts (user needs to select a date)
        if (!startDate && !endDate) {
            return counts;
        }

        const startDateObj = startDate ? new Date(startDate) : null;
        const endDateObj = endDate ? new Date(endDate) : null;
        if (startDateObj) startDateObj.setHours(0, 0, 0, 0);
        if (endDateObj) endDateObj.setHours(23, 59, 59, 999);

        rawRideData.forEach(row => {
            // Find date column - match h3Helper logic
            const dateKey = Object.keys(row).find(k => ['date', 'ride_date', 'ride date'].includes(k.toLowerCase())) ||
                Object.keys(row).find(k => k.toLowerCase().includes('date'));
            const dateCol = row.date || row.Date || row.dateCreated || row.rideDate || (dateKey ? row[dateKey] : undefined);

            if (!dateCol) return;

            let rowDate = null;
            const dateStr = dateCol.toString().trim();

            // Parse DD/MM/YYYY or DD-MM-YYYY format
            const ddmmMatch = dateStr.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
            if (ddmmMatch) {
                rowDate = new Date(parseInt(ddmmMatch[3]), parseInt(ddmmMatch[2]) - 1, parseInt(ddmmMatch[1]));
            } else if (!isNaN(Number(dateStr)) && Number(dateStr) > 25568) {
                // Excel serial date
                const utc_value = (Number(dateStr) - 25569) * 86400;
                const date_info = new Date(utc_value * 1000);
                rowDate = new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate());
            } else {
                // Try standard date parsing
                const parsed = new Date(dateStr);
                if (!isNaN(parsed.getTime())) {
                    rowDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
                }
            }

            if (!rowDate || isNaN(rowDate.getTime())) return;

            // Check if row is within date range
            rowDate.setHours(12, 0, 0, 0);

            if (startDateObj && rowDate < startDateObj) return;
            if (endDateObj && rowDate > endDateObj) return;

            // Count by status
            const status = (row.rideStatus || row.status || row.ride_status || '').toString().toUpperCase();
            if (status === 'COMPLETED') counts.completed++;
            if (status === 'CANCELLED') counts.cancelled++;
            if (status === 'DRIVER_NOT_FOUND' || status === 'DNF') counts.driverNotFound++;
        });

        return counts;
    }, [rawRideData, startDate, endDate]);

    // Initialize fleet size recommendation when data loads
    useEffect(() => {
        if (currentBaseClusters.length > 0 && fleetSize === 0) {
            const ideal = currentBaseClusters.reduce((sum, c) => sum + c.requiredSupply, 0);
            setFleetSize(Math.round(ideal * 0.8));
        }
    }, [currentBaseClusters]);

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setAppState(AppState.PROCESSING);
        const filename = file.name;

        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = e.target?.result;
                if (data) {
                    try {
                        const workbook = read(data, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const jsonData = utils.sheet_to_json(worksheet, { raw: true });

                        setRawRideData(jsonData);

                        // Save to localStorage and backend for persistence
                        saveDataToStorage(jsonData, filename);

                        // Process splits with date filter if set
                        const dateFilter = startDate || endDate ? {
                            startDate: startDate ? new Date(startDate) : null,
                            endDate: endDate ? new Date(endDate) : null,
                            timeView: timeView,
                            selectedDates: []
                        } : undefined;

                        const processed = processRideData(jsonData, config, dateFilter, locationType, startHour, endHour);
                        setBaseClustersAll(processed.allDay);
                        setBaseClustersMorning(processed.morning);
                        setBaseClustersEvening(processed.evening);
                        setDebugStats(processed.debugStats);

                        setAppState(AppState.DASHBOARD);
                    } catch (error) {
                        console.error("Excel Error", error);
                        alert("Error parsing Excel file");
                        setAppState(AppState.UPLOAD);
                    }
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    const rawData = results.data;
                    setRawRideData(rawData);

                    // Save to localStorage and backend for persistence
                    saveDataToStorage(rawData, filename);

                    // Process splits with date filter if set
                    const dateFilter = startDate || endDate ? {
                        startDate: startDate ? new Date(startDate) : null,
                        endDate: endDate ? new Date(endDate) : null,
                        timeView: timeView,
                        selectedDates: []
                    } : undefined;

                    const processed = processRideData(rawData, config, dateFilter, locationType, startHour, endHour);
                    setBaseClustersAll(processed.allDay);
                    setBaseClustersMorning(processed.morning);
                    setBaseClustersEvening(processed.evening);
                    setDebugStats(processed.debugStats);

                    // Build status-based clusters for map views


                    setAppState(AppState.DASHBOARD);
                },
                error: (err) => {
                    console.error("CSV Error", err);
                    alert("Error parsing CSV");
                    setAppState(AppState.UPLOAD);
                }
            });
        }
    };

    // Apply date filter when dates or timeView changes
    useEffect(() => {
        if (rawRideData.length > 0) {
            const dateFilter = startDate || endDate ? {
                startDate: startDate ? new Date(startDate) : null,
                endDate: endDate ? new Date(endDate) : null,
                timeView: timeView,
                selectedDates: []
            } : undefined;

            const processed = processRideData(rawRideData, config, dateFilter, locationType, startHour, endHour);
            setBaseClustersAll(processed.allDay);
            setBaseClustersMorning(processed.morning);
            setBaseClustersEvening(processed.evening);
            setDebugStats(processed.debugStats);

            // Rebuild status clusters if location type changes

        }
    }, [startDate, endDate, timeView, config, locationType, startHour, endHour]);

    // Download Handler for Supply Plans
    const handleDownloadSupplyPlan = (type) => {
        let targetClusters = [];
        let filename = "supply_plan.csv";

        switch (type) {
            case 'MORNING':
                targetClusters = isFleetConstrained ? optimizeFleetDistribution(baseClustersMorning, fleetSize) : baseClustersMorning;
                filename = "morning_supply_plan_chennai.csv";
                break;
            case 'EVENING':
                targetClusters = isFleetConstrained ? optimizeFleetDistribution(baseClustersEvening, fleetSize) : baseClustersEvening;
                filename = "evening_supply_plan_chennai.csv";
                break;
            default:
                targetClusters = optimizedClusters;
                filename = "full_day_supply_plan_chennai.csv";
                break;
        }

        const csvContent = generateCSVContent(targetClusters);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadJSON = () => {
        const jsonContent = generateJSONContent(optimizedClusters);
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'ride_clusters.json');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadStatus = (status, filename) => {
        const csvContent = generateStatusCSV(rawRideData, status, config);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadPickupDrop = (mode) => {
        const csvContent = generatePickupDropPointCSV(rawRideData, mode);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', mode === 'PICKUP' ? 'pickup_points.csv' : 'drop_points.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const handleDownloadRawDataWithHex = () => {
        const csvContent = generateRawDataWithHexCSV(rawRideData, config);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'raw_data_with_hex_and_distance.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadHexIds = () => {
        const hexIdsData = generateHexIdsData(rawRideData, config);
        if (hexIdsData.length === 0) {
            alert('No valid hex IDs found in the data');
            return;
        }

        // Create worksheet from data
        const worksheet = utils.json_to_sheet(hexIdsData);

        // Create workbook and add worksheet
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Hex IDs');

        // Generate filename with date
        const date = new Date().toISOString().split('T')[0];
        const filename = `hex_ids_${date}.xlsx`;

        // Download file
        writeFile(workbook, filename);
    };

    const fetchAiInsights = async () => {
        setLoadingAi(true);
        const insight = await getPacingStrategy(optimizedClusters, totalDemand, totalAllocatedSupply);
        setAiInsight(insight);
        setLoadingAi(false);
    };

    // Prepare chart data (Top 10 hotspots)
    const chartData = useMemo(() => {
        return optimizedClusters.slice(0, 10).map(c => ({
            name: c.hexId.substring(0, 6) + '...',
            Demand: c.demand,
            Ideal: c.requiredSupply,
            Allocated: c.allocatedSupply
        }));
    }, [optimizedClusters]);

    // Prepare line chart data for SLA Trend
    const slaTrendData = useMemo(() => {
        return optimizedClusters.slice(0, 10).map(c => ({
            name: c.hexId.substring(0, 6) + '...',
            fullHex: c.hexId,
            Score: c.slaComplianceScore,
            Demand: c.demand
        }));
    }, [optimizedClusters]);

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900">
            <main className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {appState === AppState.UPLOAD && (
                    // UPLOAD_PLACEHOLDER
                    <div className="max-w-2xl mx-auto mt-20">
                        <div className="glass-card rounded-3xl p-12 text-center relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
                            <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-300">
                                <Upload className="w-10 h-10 text-indigo-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900 mb-4 font-display">Upload Ride Data</h2>
                            <p className="text-slate-500 mb-10 text-lg max-w-md mx-auto leading-relaxed">
                                Upload your CSV file containing ride coordinates and timestamps. We&apos;ll generate optimized supply plans for Chennai.
                            </p>

                            <div className="relative max-w-md mx-auto space-y-4">
                                {/* Select File Button */}
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept=".csv,.xlsx,.xls"
                                        onChange={handleFileUpload}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    />
                                    <button className="w-full btn-primary py-4 px-8 rounded-xl font-semibold text-lg flex items-center justify-center gap-3">
                                        <FileText className="w-5 h-5" />
                                        Select File
                                    </button>
                                </div>

                                {/* Load Previous File Button */}
                                {!isCheckingPrevious && previousFileMetadata && (
                                    <button
                                        onClick={handleLoadPreviousFile}
                                        disabled={isLoadingPrevious}
                                        className="w-full py-4 px-8 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-emerald-700 border border-emerald-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoadingPrevious ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <History className="w-5 h-5" />
                                                Load Recent File
                                            </>
                                        )}
                                    </button>
                                )}

                                {/* Recent file info */}
                                {!isCheckingPrevious && previousFileMetadata && (
                                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                                        <div className="flex items-center gap-2 mb-1">
                                            <FileText className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium truncate">{previousFileMetadata.filename}</span>
                                            {previousFileMetadata.isDefault && (
                                                <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">Default</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-400">
                                            {previousFileMetadata.recordCount.toLocaleString()} records •
                                            {previousFileMetadata.isDefault ? 'System default data' : `Uploaded ${new Date(previousFileMetadata.uploadedAt).toLocaleDateString()}`}
                                        </div>
                                    </div>
                                )}

                                <p className="text-xs text-slate-400 mt-4">Supported formats: .csv, .xlsx, .xls (Max 10MB)</p>
                            </div>
                        </div>
                    </div>
                )}

                {appState === AppState.PROCESSING && (
                    <div className="flex flex-col items-center justify-center h-[60vh]">
                        <Spinner />
                        <p className="mt-4 text-slate-500 font-medium animate-pulse">Processing Ride Data...</p>
                    </div>
                )}

                {appState === AppState.DASHBOARD && (
                    <div className="relative animate-fade-in">
                        {/* Toggle Sidebar Button - Always visible */}
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="fixed right-4 top-24 z-50 p-2 bg-white rounded-lg shadow-lg border border-slate-200 hover:bg-slate-50 transition-all"
                            title={isSidebarOpen ? 'Close Filters' : 'Open Filters'}
                        >
                            {isSidebarOpen ? (
                                <PanelRightClose className="w-5 h-5 text-slate-600" />
                            ) : (
                                <PanelRightOpen className="w-5 h-5 text-slate-600" />
                            )}
                        </button>

                        {/* Right Sidebar - Collapsible */}
                        <div className={`fixed right-0 top-16 h-[calc(100vh-4rem)] bg-white/95 backdrop-blur-xl border-l border-slate-200 shadow-xl z-40 transition-all duration-300 ease-in-out overflow-y-auto ${isSidebarOpen ? 'w-80 translate-x-0' : 'w-80 translate-x-full'}`}>
                            <div className="p-4 space-y-6">
                                {/* Header */}
                                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                        <Filter className="w-5 h-5 text-indigo-600" />
                                        Filters & Controls
                                    </h3>
                                </div>

                                {/* Time View Selection */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Time Period</label>
                                    <div className="flex flex-col gap-2">
                                        {(['ALL', 'MORNING', 'EVENING']).map((view) => (
                                            <button
                                                key={view}
                                                onClick={() => setDraftTimeView(view)}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${draftTimeView === view
                                                    ? 'bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 border border-indigo-200 shadow-sm'
                                                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-transparent'
                                                    }`}
                                            >
                                                {view === 'ALL' && <Globe className="w-4 h-4" />}
                                                {view === 'MORNING' && <Sun className="w-4 h-4 text-amber-500" />}
                                                {view === 'EVENING' && <Moon className="w-4 h-4 text-purple-500" />}
                                                <span className="text-sm font-semibold">
                                                    {view === 'ALL' ? 'All Day' : view.charAt(0) + view.slice(1).toLowerCase()}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Date Range Selection */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Date Range</label>
                                    <DateRangePicker
                                        startDate={draftStartDate}
                                        endDate={draftEndDate}
                                        onRangeChange={(start, end) => {
                                            setDraftStartDate(start);
                                            setDraftEndDate(end);
                                        }}
                                    />
                                </div>

                                {/* Hour Range Selection */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Hour Range</label>
                                    <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl">
                                        <div className="flex-1">
                                            <span className="text-xs text-slate-400 font-bold block mb-1">FROM</span>
                                            <select
                                                value={draftStartHour}
                                                onChange={(e) => setDraftStartHour(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))}
                                                className="w-full border border-slate-200 bg-white text-sm font-medium rounded-lg px-2 py-1.5 text-slate-600 cursor-pointer"
                                            >
                                                <option value="ALL">All</option>
                                                {[...Array(24).keys()].map(h => (
                                                    <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                                                ))}
                                            </select>
                                        </div>
                                        <span className="text-slate-300 pt-4">→</span>
                                        <div className="flex-1">
                                            <span className="text-xs text-slate-400 font-bold block mb-1">TO</span>
                                            <select
                                                value={draftEndHour}
                                                onChange={(e) => setDraftEndHour(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value))}
                                                className="w-full border border-slate-200 bg-white text-sm font-medium rounded-lg px-2 py-1.5 text-slate-600 cursor-pointer"
                                            >
                                                <option value="ALL">All</option>
                                                {[...Array(24).keys()].map(h => (
                                                    <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Apply Button */}
                                <div className="pt-4 border-t border-slate-200">
                                    <button
                                        onClick={applyFilters}
                                        className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-500/30 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                                    >
                                        APPLY FILTERS
                                    </button>
                                </div>

                                {/* Download Supply Plans */}
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Export Supply Plans</label>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handleDownloadSupplyPlan('MORNING')}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-amber-100/50 hover:from-amber-100 hover:to-amber-200 text-amber-800 rounded-xl transition-all group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Sun className="w-4 h-4 text-amber-600" />
                                                <span className="text-sm font-semibold">Morning Plan</span>
                                            </div>
                                            <Download className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                        <button
                                            onClick={() => handleDownloadSupplyPlan('EVENING')}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100/50 hover:from-purple-100 hover:to-purple-200 text-purple-800 rounded-xl transition-all group"
                                        >
                                            <div className="flex items-center gap-3">
                                                <Moon className="w-4 h-4 text-purple-600" />
                                                <span className="text-sm font-semibold">Evening Plan</span>
                                            </div>
                                            <Download className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                        <button
                                            onClick={() => handleDownloadSupplyPlan('ALL')}
                                            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl transition-all group border border-slate-100"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FileDown className="w-4 h-4 text-slate-500" />
                                                <span className="text-sm font-semibold">Full Day Plan</span>
                                            </div>
                                            <Download className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                    </div>
                                </div>

                                {/* Upload New File */}
                                <div className="space-y-2 pt-4 border-t border-slate-200">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Data Management</label>
                                    <button
                                        onClick={handleUploadNewFile}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all border border-slate-200"
                                    >
                                        <Upload className="w-4 h-4" />
                                        <span className="text-sm font-semibold">Upload New File</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main Content Area */}
                        <div className={`space-y-8 transition-all duration-300 ${isSidebarOpen ? 'mr-80' : 'mr-0'}`}>

                            {/* Geospatial Map Visualization */}
                            <div className="glass-panel rounded-2xl shadow-xl border-0" style={{ overflow: 'visible' }}>
                                <div className="p-4 border-b border-slate-100/50 bg-white/50 backdrop-blur-md flex flex-col md:flex-row justify-between items-center gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex bg-slate-100 p-1 rounded-lg">
                                            <button
                                                onClick={() => setLocationType('PICKUP')}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${locationType === 'PICKUP' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                    }`}
                                            >
                                                Pickup
                                            </button>
                                            <button
                                                onClick={() => setLocationType('DROP')}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${locationType === 'DROP' ? 'bg-white text-pink-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                    }`}
                                            >
                                                Drop
                                            </button>
                                        </div>

                                        {/* Map Metric Toggle */}
                                        <div className="flex bg-slate-100 p-1 rounded-lg">
                                            <button
                                                onClick={() => setMapMetric('DEMAND')}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${mapMetric === 'DEMAND' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                    }`}
                                            >
                                                <Activity className="w-3 h-3" /> Demand
                                            </button>
                                            <button
                                                onClick={() => setMapMetric('SLA')}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-2 ${mapMetric === 'SLA' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                                    }`}
                                            >
                                                <Layers className="w-3 h-3" /> SLA
                                            </button>
                                        </div>

                                        {/* Service Area Toggle */}
                                        <button
                                            onClick={() => setShowServiceArea(!showServiceArea)}
                                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-2 border ${showServiceArea
                                                ? 'bg-indigo-50 text-indigo-600 border-indigo-200 shadow-sm'
                                                : 'bg-slate-50 text-slate-500 border-slate-200 hover:text-slate-700 hover:bg-slate-100'
                                                }`}
                                        >
                                            <MapPin className="w-3 h-3" />
                                            Service Area: Fish Bowl ({showServiceArea ? 'ON' : 'OFF'})
                                        </button>

                                        {/* Map Status Filter - Multi-Select Pills */}
                                        <div className="flex flex-wrap gap-2 bg-slate-100 p-2 rounded-xl max-w-2xl">
                                            {[
                                                { id: 'ALL', label: 'All' },
                                                { id: 'FISH_BOWL', label: 'Fish Bowl' },
                                                { id: 'COMPLETED', label: 'Completed' },
                                                { id: 'CANCELLED', label: 'Cancelled' },
                                                { id: 'DRIVER_NOT_FOUND', label: 'DNF' },
                                                { id: 'PENDING', label: 'Pending' },
                                                { id: 'DRIVER_ASSIGNED', label: 'Assigned' },
                                                { id: 'OUTSIDE_DROP_AREA', label: 'Outside Drop' },
                                                { id: 'OUTSIDE_PICKUP_AREA', label: 'Outside Pickup' },
                                                { id: 'OUTSIDE_AREA', label: 'Outside Area' }
                                            ].map((option) => {
                                                const isActive = mapStatusFilter.includes(option.id);
                                                return (
                                                    <button
                                                        key={option.id}
                                                        onClick={() => {
                                                            if (option.id === 'ALL') {
                                                                setMapStatusFilter(['ALL']);
                                                            } else {
                                                                let newFilters = mapStatusFilter.filter(f => f !== 'ALL');
                                                                if (newFilters.includes(option.id)) {
                                                                    newFilters = newFilters.filter(f => f !== option.id);
                                                                } else {
                                                                    newFilters.push(option.id);
                                                                }
                                                                if (newFilters.length === 0) newFilters = ['ALL'];
                                                                setMapStatusFilter(newFilters);
                                                            }
                                                        }}
                                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all border ${isActive
                                                            ? 'bg-white text-indigo-600 border-indigo-200 shadow-sm'
                                                            : 'bg-transparent text-slate-500 border-transparent hover:bg-white/50 hover:text-slate-700'
                                                            }`}
                                                    >
                                                        {option.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full relative" style={{ height: '600px' }}>
                                    <MapVisualizer
                                        clusters={mapClusters}
                                        metric={mapMetric}
                                        showServiceArea={showServiceArea}
                                        onToggleServiceArea={() => setShowServiceArea(prev => !prev)}
                                        hexOpacity={hexOpacity}
                                        onOpacityChange={setHexOpacity}
                                        kmzOverlay={kmzOverlay}
                                        onKmzUpload={setKmzOverlay}
                                        onKmzToggle={() => setKmzOverlay(prev => prev ? { ...prev, visible: !prev.visible } : null)}
                                        onKmzRemove={() => setKmzOverlay(null)}
                                        showWeirdArea={showWeirdArea}
                                        onToggleWeirdArea={() => setShowWeirdArea(prev => !prev)}
                                        weirdAreaCoords={weirdAreaCoords}
                                        onUpdateWeirdArea={setWeirdAreaCoords}
                                    />
                                </div>
                            </div>

                            {/* Date Range Summary */}
                            <div className="glass-card p-5 rounded-2xl">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-50 rounded-lg">
                                            <Filter className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Selected Period</span>
                                            <p className="text-sm font-bold text-slate-800">
                                                {startDate || endDate ? (
                                                    startDate === endDate || (!endDate && startDate) || (!startDate && endDate) ? (
                                                        new Date(startDate || endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                    ) : (
                                                        `${new Date(startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${new Date(endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                    )
                                                ) : (
                                                    'All Dates'
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                            <div>
                                                <span className="text-xs text-slate-400 font-medium">Completed</span>
                                                <p className="text-lg font-bold text-green-600">{dateFilteredStats.completed.toLocaleString()}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <div>
                                                <span className="text-xs text-slate-400 font-medium">Cancelled</span>
                                                <p className="text-lg font-bold text-red-600">{dateFilteredStats.cancelled.toLocaleString()}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                                            <div>
                                                <span className="text-xs text-slate-400 font-medium">Driver Not Found</span>
                                                <p className="text-lg font-bold text-amber-600">{dateFilteredStats.driverNotFound.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            {/* Cluster Table */}
                            <div className="glass-card rounded-2xl overflow-hidden">
                                <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                        <LayoutDashboard className="w-5 h-5 text-indigo-600" />
                                        Cluster Details
                                    </h3>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={handleDownloadHexIds}
                                            disabled={rawRideData.length === 0}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            data-testid="download-hex-ids-button"
                                        >
                                            <FileSpreadsheet className="w-4 h-4" />
                                            Download Hex IDs
                                        </button>
                                        <div className="text-xs font-medium px-2 py-1 bg-slate-200 rounded-md text-slate-600">
                                            {displayClusters.length} Clusters
                                        </div>
                                    </div>
                                </div>
                                <div className="overflow-x-auto max-h-[600px] overflow-y-auto custom-scrollbar">
                                    <table className="w-full text-sm text-left text-slate-500">
                                        <thead className="text-xs text-slate-700 uppercase bg-slate-50/80 backdrop-blur sticky top-0 z-10">
                                            <tr>
                                                <th
                                                    className="px-6 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                                                    onClick={() => {
                                                        setSortField('hexId');
                                                        setSortDirection(sortField === 'hexId' && sortDirection === 'asc' ? 'desc' : 'asc');
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Hex ID {sortField === 'hexId' && (sortDirection === 'asc' ? <ArrowRight className="w-3 h-3 rotate-90" /> : <ArrowRight className="w-3 h-3 -rotate-90" />)}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                                                    onClick={() => {
                                                        setSortField('demand');
                                                        setSortDirection(sortField === 'demand' && sortDirection === 'desc' ? 'asc' : 'desc');
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Demand {sortField === 'demand' && (sortDirection === 'desc' ? <ArrowRight className="w-3 h-3 rotate-90" /> : <ArrowRight className="w-3 h-3 -rotate-90" />)}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                                                    onClick={() => {
                                                        setSortField('demandScore');
                                                        setSortDirection(sortField === 'demandScore' && sortDirection === 'desc' ? 'asc' : 'desc');
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Score {sortField === 'demandScore' && (sortDirection === 'desc' ? <ArrowRight className="w-3 h-3 rotate-90" /> : <ArrowRight className="w-3 h-3 -rotate-90" />)}
                                                    </div>
                                                </th>
                                                <th className="px-6 py-3">Ideal Supply</th>
                                                <th
                                                    className="px-6 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                                                    onClick={() => {
                                                        setSortField('allocatedSupply');
                                                        setSortDirection(sortField === 'allocatedSupply' && sortDirection === 'desc' ? 'asc' : 'desc');
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Allocated {sortField === 'allocatedSupply' && (sortDirection === 'desc' ? <ArrowRight className="w-3 h-3 rotate-90" /> : <ArrowRight className="w-3 h-3 -rotate-90" />)}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 cursor-pointer hover:bg-slate-100 transition-colors"
                                                    onClick={() => {
                                                        setSortField('slaComplianceScore');
                                                        setSortDirection(sortField === 'slaComplianceScore' && sortDirection === 'desc' ? 'asc' : 'desc');
                                                    }}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        SLA {sortField === 'slaComplianceScore' && (sortDirection === 'desc' ? <ArrowRight className="w-3 h-3 rotate-90" /> : <ArrowRight className="w-3 h-3 -rotate-90" />)}
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {displayClusters.map((c) => (
                                                <tr key={c.hexId} className="bg-white hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4 font-mono text-xs text-slate-400 group-hover:text-indigo-600 transition-colors">{c.hexId}</td>
                                                    <td className="px-6 py-4 font-bold text-slate-700">{c.demand}</td>
                                                    <td className="px-6 py-4 text-slate-500">{c.demandScore}</td>
                                                    <td className="px-6 py-4 text-slate-400">{c.requiredSupply}</td>
                                                    <td className={`px-6 py-4 font-bold ${c.allocatedSupply < c.requiredSupply ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                        {c.allocatedSupply}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-bold text-slate-600 w-8">{c.slaComplianceScore}%</span>
                                                            <div className="flex-1 bg-slate-100 rounded-full h-2 min-w-[60px] overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${c.slaComplianceScore >= 90 ? 'bg-emerald-500' :
                                                                        c.slaComplianceScore >= 70 ? 'bg-amber-500' : 'bg-rose-500'
                                                                        }`}
                                                                    style={{ width: `${c.slaComplianceScore}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Fleet Allocation Results Section */}
                            <div className="mt-12 border-t border-slate-200 pt-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                                        <LayoutDashboard className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-slate-900">Fleet Allocation Results (Fish Bowl Only)</h2>
                                        <p className="text-slate-500">Optimized distribution for the specific Fish Bowl hexes.</p>
                                    </div>
                                </div>

                                <div className="glass-card overflow-hidden rounded-2xl">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-slate-50/50 border-b border-slate-100">
                                                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Hex ID</th>
                                                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Demand</th>
                                                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Ideal Supply</th>
                                                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Allocated</th>
                                                    <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">SLA Score</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {optimizedClusters.map((cluster) => (
                                                    <tr key={cluster.hexId} className="hover:bg-slate-50/50 transition-colors">
                                                        <td className="p-4 text-sm font-medium text-slate-700 font-mono">{cluster.hexId}</td>
                                                        <td className="p-4 text-sm text-slate-600 text-right">{cluster.demand}</td>
                                                        <td className="p-4 text-sm text-slate-600 text-right">{cluster.requiredSupply}</td>
                                                        <td className="p-4 text-sm font-bold text-indigo-600 text-right">{cluster.allocatedSupply}</td>
                                                        <td className="p-4 text-sm text-right">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${cluster.slaComplianceScore >= 80 ? 'bg-green-100 text-green-700' :
                                                                cluster.slaComplianceScore >= 50 ? 'bg-amber-100 text-amber-700' :
                                                                    'bg-red-100 text-red-700'
                                                                }`}>
                                                                {cluster.slaComplianceScore}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {optimizedClusters.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5} className="p-8 text-center text-slate-400">
                                                            No data available for Fish Bowl hexes.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default HexHotspots;
